import { Drawer, Modal, message, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Archive, Plus, RefreshCw, Rocket, Star } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { applicationStore } from '@/stores';
import type { AppCatalogVersionItem } from '@/types';
import { formatBytes, formatProtoTimestamp, getStatusMeta } from '../constants';
import { VersionFormDialog } from './VersionFormDialog';

function actionKey(action: string, version: AppCatalogVersionItem) {
  return `${action}:${version.appId}:${version.versionId}`;
}

export const VersionDrawer = observer(() => {
  const [createOpen, setCreateOpen] = useState(false);
  const app = applicationStore.activeApp;

  const runAction = (
    title: string,
    content: string,
    onConfirm: () => Promise<unknown>,
  ) => {
    Modal.confirm({
      title,
      content,
      okText: '确认',
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        try {
          await onConfirm();
          message.success('操作成功');
        } catch (error) {
          console.error('版本操作失败:', error);
          message.error('操作失败');
          throw error;
        }
      },
    });
  };

  const columns: ColumnsType<AppCatalogVersionItem> = [
    {
      key: 'versionId',
      title: '版本',
      width: 150,
      fixed: 'left',
      render: (_, version) => (
        <div className="space-y-1">
          <div className="font-mono text-sm font-medium">
            {version.versionId}
          </div>
          {app?.latestVersionId === version.versionId ? (
            <Tag color="blue">latest</Tag>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      title: '状态',
      width: 100,
      render: (_, version) => {
        const meta = getStatusMeta(version.status);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      key: 'artifact',
      title: 'Artifact',
      width: 320,
      render: (_, version) => (
        <div className="max-w-[320px] space-y-1">
          <div className="truncate text-sm">{version.artifactUrl}</div>
          <div className="font-mono text-xs text-muted-foreground">
            {version.artifactKind} · {formatBytes(version.artifactSizeBytes)}
          </div>
        </div>
      ),
    },
    {
      key: 'sha',
      title: 'SHA256',
      width: 180,
      render: (_, version) => (
        <Tooltip title={version.artifactSha256}>
          <span className="block max-w-[160px] truncate font-mono text-xs">
            {version.artifactSha256}
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'runtime',
      title: 'Runtime',
      width: 190,
      render: (_, version) => (
        <div className="space-y-1 text-xs">
          <div className="font-mono">{version.runtimeBootstrap}</div>
          <div className="font-mono text-muted-foreground">
            {version.runtimeHealthcheckPath}
          </div>
        </div>
      ),
    },
    {
      key: 'updatedAt',
      title: '更新时间',
      width: 180,
      render: (_, version) => (
        <span className="text-muted-foreground">
          {formatProtoTimestamp(version.updatedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, version) => {
        const isLatest = app?.latestVersionId === version.versionId;
        const canSetLatest = version.status === 'published' && !isLatest;
        const canArchive = version.status !== 'archived' && !isLatest;
        return (
          <div className="flex items-center gap-1">
            <Tooltip title="发布版本">
              <Button
                variant="ghost"
                size="sm"
                disabled={version.status === 'archived'}
                loading={
                  applicationStore.actionLoadingKey ===
                  actionKey('publish', version)
                }
                onClick={() =>
                  runAction(
                    '发布版本',
                    `确认发布 ${version.versionId}？发布会将该版本设为 latest。`,
                    () =>
                      applicationStore.publishVersion({
                        appId: version.appId,
                        versionId: version.versionId,
                      }),
                  )
                }
              >
                <Rocket className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip title="设为 latest">
              <Button
                variant="ghost"
                size="sm"
                disabled={!canSetLatest}
                loading={
                  applicationStore.actionLoadingKey ===
                  actionKey('latest', version)
                }
                onClick={() =>
                  runAction(
                    '设为 latest',
                    `确认将 latest 回滚或切换到 ${version.versionId}？`,
                    () =>
                      applicationStore.setLatestVersion({
                        appId: version.appId,
                        versionId: version.versionId,
                      }),
                  )
                }
              >
                <Star className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip title="归档版本">
              <Button
                variant="ghost"
                size="sm"
                disabled={!canArchive}
                loading={
                  applicationStore.actionLoadingKey ===
                  actionKey('archive', version)
                }
                onClick={() =>
                  runAction(
                    '归档版本',
                    `确认归档 ${version.versionId}？当前 latest 版本不能归档。`,
                    () =>
                      applicationStore.archiveVersion({
                        appId: version.appId,
                        versionId: version.versionId,
                      }),
                  )
                }
              >
                <Archive className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        open={Boolean(app)}
        onClose={applicationStore.closeVersions}
        title={app ? `版本管理 - ${app.displayName}` : '版本管理'}
        width={980}
        extra={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => void applicationStore.fetchVersions()}
              loading={applicationStore.versionsLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              刷新
            </Button>
            <Button onClick={() => setCreateOpen(true)} disabled={!app}>
              <Plus className="mr-2 h-4 w-4" />
              新增版本
            </Button>
          </div>
        }
      >
        <Table<AppCatalogVersionItem>
          rowKey="id"
          columns={columns}
          dataSource={applicationStore.versions}
          loading={applicationStore.versionsLoading}
          pagination={false}
          size="middle"
          scroll={{ x: 1220 }}
        />
      </Drawer>

      <VersionFormDialog
        open={createOpen}
        app={app}
        onOpenChange={setCreateOpen}
      />
    </>
  );
});
